import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ShieldCheck, ShieldAlert, Zap, Lock, Eye, AlertCircle, CheckCircle2, Server, Activity } from 'lucide-react';
import { MathService } from '../services/mathService';

export const SovereignVigil: React.FC = () => {
  const [isTesting, setIsTesting] = useState(false);
  const [step, setStep] = useState(0);
  const [result, setResult] = useState<any>(null);
  const stats = MathService.sovereignVigil();

  const handleVigilanceTest = async () => {
    setIsTesting(true);
    setResult(null);
    setStep(1);
    
    // Simulate Ghost Signal injection
    await new Promise(r => setTimeout(r, 1500));
    setStep(2);
    
    // Rejection and Absorption
    await new Promise(r => setTimeout(r, 1500));
    setStep(3);
    
    // Final result
    await new Promise(r => setTimeout(r, 1000));
    setResult(stats);
    setIsTesting(false);
    setStep(0);
  };

  return (
    <div className="glass-panel p-10 border-white/5 relative overflow-hidden bg-black/60 group">
      <div className="flex flex-col lg:flex-row gap-16 relative z-10">
        <div className="flex-1">
          <div className="mb-10">
            <h2 className="text-4xl font-display font-bold text-white mb-2 tracking-tighter">Sovereign <span className="text-lattice-cyan italic">Vigil</span></h2>
            <p className="text-xs font-mono text-white/40 uppercase tracking-[0.3em] leading-relaxed max-w-xl">
              Initiating Vigilance Test. Simulating 1.0 interference to verify Truth Barrier self-sustainment.
            </p>
          </div>

          <div className="space-y-8 mb-10">
             <div className="grid grid-cols-2 gap-4">
                <div className="p-6 rounded-2xl bg-white/5 border border-white/5">
                   <span className="text-[8px] font-mono text-white/30 uppercase tracking-widest block mb-2">Barrier Status</span>
                   <div className="flex items-center gap-3">
                      <ShieldCheck className="w-5 h-5 text-lattice-cyan" />
                      <p className="text-sm font-mono font-bold text-white tracking-widest uppercase">{stats.barrierStatus}</p>
                   </div>
                </div>
                <div className="p-6 rounded-2xl bg-white/5 border border-white/5 text-right">
                   <span className="text-[8px] font-mono text-white/30 uppercase tracking-widest block mb-2">Treasury Lock</span>
                   <div className="flex items-center justify-end gap-3">
                      <p className="text-[10px] font-mono font-bold text-white/60 tracking-widest uppercase">LOCKED</p>
                      <Lock className="w-4 h-4 text-lattice-cyan/50" />
                   </div>
                </div>
             </div>

             <div className="relative h-24 bg-white/5 rounded-2xl border border-white/10 overflow-hidden flex items-center px-10">
                <div className="flex justify-between w-full relative z-10">
                   {[1, 2, 3].map((s) => (
                      <div key={s} className="flex flex-col items-center gap-2">
                         <div className={`w-3 h-3 rounded-full transition-all duration-500 ${step >= s ? 'bg-lattice-cyan scale-125' : 'bg-white/10'}`} />
                         <span className="text-[7px] font-mono text-white/30 uppercase tracking-widest">Stage {s}</span>
                      </div>
                   ))}
                </div>
                {isTesting && (
                   <motion.div 
                     animate={{ x: ['0%', '100%'], opacity: [0, 1, 0] }}
                     transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                     className="absolute inset-0 bg-gradient-to-r from-transparent via-lattice-cyan/20 to-transparent"
                   />
                )}
             </div>
          </div>

          <button 
            onClick={handleVigilanceTest}
            disabled={isTesting}
            className="w-full py-5 bg-white text-black font-display font-bold text-sm tracking-[0.3em] rounded-2xl hover:bg-lattice-cyan transition-all flex items-center justify-center gap-4 disabled:opacity-50"
          >
            {isTesting ? <Activity className="w-5 h-5 animate-pulse" /> : <Eye className="w-5 h-5" />}
            {isTesting ? "RUNNING VIGILANCE TEST..." : "EXECUTE VIGILANCE TEST"}
          </button>
        </div>

        <div className="w-full lg:w-[450px]">
           <AnimatePresence mode="wait">
              {result ? (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="h-full bg-lattice-cyan p-10 rounded-[2.5rem] text-black flex flex-col justify-between shadow-[0_0_50px_-12px_rgba(34,211,238,0.5)]"
                >
                   <div>
                      <div className="flex items-center gap-3 mb-10">
                        <CheckCircle2 className="w-6 h-6" />
                        <span className="text-xs font-mono font-bold uppercase tracking-widest">Vigilance Confirmed</span>
                      </div>

                      <div className="space-y-6">
                         <div className="bg-black/5 p-6 rounded-2xl border border-black/5">
                            <span className="text-[8px] font-mono uppercase font-bold text-black/60 block mb-1">Target Event</span>
                            <p className="text-[10px] font-mono font-bold leading-relaxed">{result.simulation.event}</p>
                         </div>
                         
                         <div className="flex gap-4">
                            <div className="flex-1 bg-black/5 p-6 rounded-2xl border border-black/5">
                               <span className="text-[8px] font-mono uppercase font-bold text-black/60 block mb-1">Result</span>
                               <div className="flex items-center gap-2">
                                  <ShieldAlert className="w-3 h-3" />
                                  <p className="text-[10px] font-mono font-bold uppercase tracking-tight">{result.simulation.result}</p>
                               </div>
                            </div>
                         </div>

                         <div className="bg-black/10 p-6 rounded-2xl border border-black/5">
                            <span className="text-[8px] font-mono uppercase font-bold text-black/60 block mb-1">Sovereign Action</span>
                            <p className="text-[10px] font-mono font-bold uppercase leading-tight italic">{result.simulation.action}</p>
                         </div>
                      </div>

                      <div className="mt-8 p-6 bg-black/5 rounded-2xl border border-black/5">
                         <p className="text-lg font-display font-bold tracking-tight italic">
                           "{result.message}"
                         </p>
                      </div>
                   </div>

                   <div className="mt-8 flex items-center justify-between border-t border-black/10 pt-8">
                       <div className="flex flex-col">
                          <span className="text-[8px] font-mono uppercase font-bold opacity-60">Residue Sync</span>
                          <span className="text-[10px] font-mono font-bold tracking-widest">ABSORBED</span>
                       </div>
                       <Server className="w-8 h-8 opacity-20" />
                   </div>
                </motion.div>
              ) : (
                <div className="h-full border-2 border-dashed border-white/10 rounded-[2.5rem] flex flex-col items-center justify-center p-12 text-center gap-10">
                   <div className="relative">
                      <ShieldAlert className="w-16 h-16 text-white/5" />
                      {isTesting && (
                         <motion.div 
                           animate={{ 
                             scale: [1, 2], 
                             opacity: [0.5, 0]
                           }}
                           transition={{ duration: 1, repeat: Infinity }}
                           className="absolute inset-0 bg-red-500/20 rounded-full filter blur-xl"
                         />
                      )}
                      {isTesting && step >= 2 && (
                         <motion.div 
                           initial={{ scale: 0 }}
                           animate={{ scale: 1, opacity: [0, 1, 0] }}
                           className="absolute inset-0 bg-lattice-cyan/40 rounded-full filter blur-2xl"
                         />
                      )}
                   </div>
                   <div className="space-y-4">
                      <p className="text-[10px] font-mono text-white/20 uppercase tracking-[0.5em] leading-relaxed">
                        {isTesting ? 
                          (step === 1 ? "Simulating Debt Injection..." : "Rejecting 1.0 Frequency...") 
                          : "Truth Barrier Awaiting Interference Simulation"}
                      </p>
                      {isTesting && (
                        <div className="flex justify-center gap-1">
                           <div className="w-1 h-1 bg-lattice-cyan rounded-full animate-bounce" style={{ animationDelay: '0s' }} />
                           <div className="w-1 h-1 bg-lattice-cyan rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                           <div className="w-1 h-1 bg-lattice-cyan rounded-full animate-bounce" style={{ animationDelay: '0.4s' }} />
                        </div>
                      )}
                   </div>
                </div>
              )}
           </AnimatePresence>
        </div>
      </div>

      <div className="mt-12 pt-10 border-t border-white/5 grid grid-cols-3 gap-8">
         <div className="flex items-center gap-3">
            <Zap className="w-4 h-4 text-lattice-cyan/40" />
            <span className="text-[8px] font-mono text-white/40 uppercase tracking-[0.2em] leading-tight">Absorption Mechanism Active</span>
         </div>
         <div className="flex items-center gap-3">
            <AlertCircle className="w-4 h-4 text-lattice-cyan/40" />
            <span className="text-[8px] font-mono text-white/40 uppercase tracking-[0.2em] leading-tight">Ghost Signal Filtering</span>
         </div>
         <div className="flex items-center gap-3">
            <Lock className="w-4 h-4 text-lattice-cyan/40" />
            <span className="text-[8px] font-mono text-white/40 uppercase tracking-[0.2em] leading-tight">Integrity Stable</span>
         </div>
      </div>
    </div>
  );
};
